import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useState } from "react";
import { Navigation, X, LocateFixed, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { detectPlace } from "../lib/nearby";

const ALL = "__all__";

/** GPS do telemóvel/browser → concelho mais próximo → centro da pesquisa por raio. */
export function useGpsCenter(setFilters, radius) {
  const [locating, setLocating] = useState(false);
  const [gpsPlace, setGpsPlace] = useState(null);
  const locateMe = async () => {
    if (locating) return;
    setLocating(true);
    try {
      const place = await detectPlace();
      setGpsPlace(place);
      setFilters({ near_district: place.district, near_concelho: place.concelho, radius_km: radius || "25",
        district: "", concelho: "" });
      toast.success(`Localização detetada: ${place.concelho}`, { description: `A mostrar vagas até ${radius || 25} km.` });
    } catch (e) {
      toast.error(e?.message || "Não conseguimos obter a tua localização.");
    } finally {
      setLocating(false);
    }
  };
  return { locating, gpsPlace, locateMe };
}

/** Botão compacto "Perto de mim" (GPS) para telemóvel, junto ao botão Filtros. */
export function GpsNearButton({ setFilters, radius, className = "" }) {
  const { locating, locateMe } = useGpsCenter(setFilters, radius);
  return (
    <Button data-testid="gps-near-btn" variant="outline" disabled={locating} onClick={locateMe}
      className={`rounded-full h-11 border-sky-200 text-sky-700 hover:bg-sky-50 ${className}`}>
      {locating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <LocateFixed className="w-4 h-4 mr-1.5" />}
      Perto de mim
    </Button>
  );
}

/**
 * Filtro "perto de mim": concelho de referência + raio em km.
 * Pré-preenche com o concelho do perfil do candidato quando existe.
 */
export const DistanceFilter = ({ meta, get, setFilters, districtCounts }) => {
  const { user } = useAuth();
  const nearDistrict = get("near_district");
  const nearConcelho = get("near_concelho");
  const radius = get("radius_km");
  const radiusOptions = meta.radius_options || [10, 25, 50, 100];
  const concelhos = nearDistrict ? meta.districts[nearDistrict] || [] : [];
  const profileConcelho = user && user !== false ? user.concelho : null;
  const profileDistrict = user && user !== false ? user.district : null;

  // Regras de sincronização com os filtros de localização da página:
  //  - só distrito de referência  -> filtra por esse distrito (district = distrito escolhido)
  //  - concelho de referência     -> pesquisa por raio (atravessa fronteiras de distrito,
  //                                  por isso limpa district/concelho)
  const chooseDistrict = (d) =>
    setFilters({ near_district: d, near_concelho: "", radius_km: "", district: d, concelho: "" });

  const chooseConcelho = (c) => {
    if (!c) {
      setFilters({ near_concelho: "", radius_km: "", district: nearDistrict, concelho: "" });
      return;
    }
    setFilters({ near_concelho: c, radius_km: radius || "25", district: "", concelho: "" });
  };

  const useMyLocation = () =>
    setFilters({ near_district: profileDistrict || "", near_concelho: profileConcelho, radius_km: radius || "25",
      district: "", concelho: "" });

  const { locating, gpsPlace, locateMe } = useGpsCenter(setFilters, radius);

  const clear = () => setFilters({ near_district: "", near_concelho: "", radius_km: "", district: "", concelho: "" });

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-5" data-testid="distance-filter">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1.5">
          <Navigation className="w-3.5 h-3.5 text-sky-600" /> Distância
        </p>
        {(nearConcelho || nearDistrict) && (
          <button data-testid="distance-clear" onClick={clear} className="text-slate-400 hover:text-slate-700 transition-colors">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <Button data-testid="use-gps-location" size="sm" disabled={locating} onClick={locateMe}
        className="w-full rounded-full mb-2 text-xs bg-sky-600 hover:bg-sky-700 text-white">
        {locating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <LocateFixed className="w-3.5 h-3.5 mr-1.5" />}
        {locating ? "A obter localização..." : "Usar a minha localização atual (GPS)"}
      </Button>
      {gpsPlace && gpsPlace.concelho === nearConcelho && (
        <p data-testid="gps-location-info" className="text-[11px] text-sky-700 mb-3 flex items-center gap-1">
          <LocateFixed className="w-3 h-3" /> Centro: {gpsPlace.concelho} (via GPS)
        </p>
      )}

      {profileConcelho && profileConcelho !== nearConcelho && (
        <Button data-testid="use-my-location" variant="outline" size="sm" className="w-full rounded-full mb-3 text-xs"
          onClick={useMyLocation}>
          Usar a minha localidade ({profileConcelho})
        </Button>
      )}

      <div className="space-y-2">
        <Select value={nearDistrict || ALL}
          onValueChange={(v) => chooseDistrict(v === ALL ? "" : v)}>
          <SelectTrigger data-testid="near-district" className="bg-white h-9 text-sm">
            <SelectValue placeholder="Distrito" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value={ALL}>Distrito de referência</SelectItem>
            {Object.keys(meta.districts).map((d) => {
              const c = districtCounts ? (districtCounts[d] || 0) : null;
              return (
                <SelectItem key={d} value={d} data-testid={`near-district-option-${d}`}>
                  {d}{c != null ? ` (${c})` : ""}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {concelhos.length > 0 && (
          <Select value={nearConcelho || ALL} onValueChange={(v) => chooseConcelho(v === ALL ? "" : v)}>
            <SelectTrigger data-testid="near-concelho" className="bg-white h-9 text-sm">
              <SelectValue placeholder="Concelho" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ALL}>Concelho onde estás</SelectItem>
              {concelhos.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-3">
        {radiusOptions.map((r) => (
          <button key={r} data-testid={`radius-${r}`} disabled={!nearConcelho}
            onClick={() => setFilters({ radius_km: String(r) })}
            className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              String(r) === radius ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-black/10 hover:border-sky-400"}`}>
            {r} km
          </button>
        ))}
      </div>

      {!nearConcelho && nearDistrict && (
        <p className="text-[11px] text-slate-500 mt-3">A mostrar vagas no distrito de {nearDistrict}. Escolhe o concelho onde estás para pesquisar por raio.</p>
      )}
      {!nearConcelho && !nearDistrict && (
        <p className="text-[11px] text-slate-400 mt-3">Escolhe o concelho onde estás para ver só vagas dentro do raio.</p>
      )}
      {nearConcelho && radius && (
        <p className="text-[11px] text-slate-500 mt-3">A mostrar vagas até {radius} km de {nearConcelho}, mais próximas primeiro.</p>
      )}
    </div>
  );
};
